import React, { useState, useEffect, useRef } from 'react';
import { Route, Routes } from "react-router-dom";


//Application Components for Login and Shared
import { EmptyAppContext } from './components/Shared/UserIdentity';
import { MobileViewContext } from './components/Shared/Viewpoint';
import Layout from './components/Shared/Layout';
import NotFound from './components/Shared/NotFound';
import SessionTimeout from './components/Shared/SessionTimeout';
import FadeConfirmation from './components/Shared/FadeConfirmation';
import RouteGuard from './components/Shared/RouteGuard';

import Login from './components/Login/Login';
import Logout from './components/Login/Logout';
import OsuLogin from './components/Login/OsuLogin';

//Application Components for features
import AdminPage from './components/Admin/AdminPage';
import UserLookup from './components/UserLookup/UserLookup';
import Dashboard from './components/Dashboard/Dashboard';
import UserUpdates from './components/UserUpdates/UserUpdates';
import Xref from './components/Xref/Xref';
import StagingTable from './components/StagingTable/StagingTable';
import Troubleshooting from './components/Troubleshooting/Troubleshooting';
import ErrorsQueue from './components/ErrorsQueue/ErrorsQueue';

//API to get client settings from server
import { SessionContextName } from './components/Shared/SharedServices';
import api from './components/Login/AuthenticationServices';
import fetchApi from './components/FetchComponents/FetchComponentsServices';

import './components/Shared/App.css'

export const AppContext = React.createContext(EmptyAppContext);

const sessionCountdownSeconds = 120;

const App = () => {
    //Save context in App state so that it can be updated later after user login
    const updateContext = (data) => {
        if (data) {
            const newContext = { ...context, ...data };
            setContext(newContext);
            //Save to session storage for page refresh
            sessionStorage.setItem(SessionContextName, JSON.stringify(data));
        } else {
            //Reset context if data is null
            setContext({ ...context, ...EmptyAppContext, updateContext, setApiMessage });
            sessionStorage.setItem(SessionContextName, null);
        }
    };

    //For API error popup
    const [apiError, setApiError] = useState({ type: undefined, message: '' });

    const setApiMessage = (message, type = 'error') => {
        setApiError({ type: `message ${type}`, message });
    }

    const [context, setContext] = useState({
        ...EmptyAppContext,
        updateContext,
        setApiMessage
    });

    //Setup for mobile view state
    const updateMobileView = (data) => {
        if (data) {
            setMobileView({ ...mobileView, isMobileView: data })
        } else {
            setMobileView({ isMobileView: false, updateMobileView: updateMobileView })
        }
    }

    const [mobileView, setMobileView] = useState({
        isMobileView: false,
        updateMobileView: updateMobileView
    });

    // For session timeout popup
    const [idleTimeOutMilSeconds, setIdleTimeOutMilSeconds] = useState(18 * 60 * 1000);
    const [resetIdleTimer, setResetIdleTimer] = useState(0);
    const [applicationName, setApplicationName] = useState("Loading ...");

    //Reload context from sessionStorage if page refresh
    useEffect(() => {
        api.clientSettings().then(data => {
            setApplicationName(data.applicationName);
            setIdleTimeOutMilSeconds((data.loginCookieExpireInSeconds - sessionCountdownSeconds) * 1000);
            setResetIdleTimer(resetIdleTimer + 1); //Reset timer
        }).catch((err) => {
            console.log(err);
            setApiError({
                type: 'message error',
                message: 'Error when getting application setting!'
            });
        });

        // Access initial context value from session storage
        let sessionContext = sessionStorage.getItem(SessionContextName);
        if (sessionContext) {
            // Update context
            let data = JSON.parse(sessionContext);
            const initContext = { ...context, ...data };
            setContext(initContext);
        }        
    }, [context.isAuthenticated]);

    const [syncOverride, setSyncOverride] = useState(false);
    const [autoCompleteLoading, setAutoCompleteLoading] = useState(false);
    const [autoCompleteData, setAutoCompleteData] = useState([]);

    const fetchSyncOverride = async () => {
        const response = await fetchApi.GetOverride()
        setSyncOverride(response === 'true')
    }

    const toggleSyncOverride = async () => {
        const response = await fetchApi.TogleSyncOverride()
        if(response === 'true') {
            fetchSyncOverride()
        }
    }

    const fetchAutoCompleteData = async () => {
        setAutoCompleteLoading(true)
        const response = await fetchApi.getAutoCompleteData()
        setAutoCompleteData(response)
        setAutoCompleteLoading(false)
    }

    const refetchAutocomplete = async () => {
        await fetchApi.RefreshAutoCompleteData().then(fetchAutoCompleteData())
    }

    useEffect(() => {
        fetchSyncOverride()
        fetchAutoCompleteData()
    }, []);

    return (
        <AppContext.Provider value={context}>
                <MobileViewContext.Provider value={mobileView}>
                    {apiError.type && <FadeConfirmation apiError={apiError} clearMessage={() => setApiError({ type: undefined, message: '' })} />}
                    <SessionTimeout
                        countdownSeconds={sessionCountdownSeconds}
                        idleTimeOutMilSeconds={idleTimeOutMilSeconds}
                        isAuthenticated={context && context.isAuthenticated}
                        resetIdleTimer={resetIdleTimer}
                    />
                    <Layout applicationName={applicationName} syncOverride={syncOverride} toggleOverride={toggleSyncOverride}>
                        <Routes>
                        <Route index element={<Login />} />
                            <Route path='/ErrorsQueue' element={<RouteGuard roles='Admin, SuperAdmin' context={context}><ErrorsQueue /></RouteGuard>} />
                            <Route path='/SyncJobLog' element={<RouteGuard roles='Admin, SuperAdmin' context={context}><Dashboard  autoCompleteData={autoCompleteData} autoCompleteLoading={autoCompleteLoading} fetchAutoComplete={fetchAutoCompleteData}  syncOverride={syncOverride} toggleOverride={toggleSyncOverride} /></RouteGuard>} />
                            <Route path='/Users' element={<RouteGuard roles='Admin, SuperAdmin' context={context}>
                                <UserLookup autoCompleteData={autoCompleteData} autoCompleteLoading={autoCompleteLoading} fetchAutoComplete={fetchAutoCompleteData} refetchAutocomplete={refetchAutocomplete} />
                                </RouteGuard >} />
                            <Route path='/UserUpdates' element={<RouteGuard roles='Admin, SuperAdmin' context={context}><UserUpdates /></RouteGuard>} />
                            <Route path='/XrefData' element={<RouteGuard roles='SuperAdmin' context={context}>
                                <Xref autoCompleteData={autoCompleteData} refetchAutocomplete={refetchAutocomplete}/>
                                </RouteGuard>} />
                            <Route path='/Staging' element={<RouteGuard roles='SuperAdmin' context={context}><StagingTable /></RouteGuard >} />
                            <Route path='/Admins' element={<RouteGuard roles='SuperAdmin' context={context}><AdminPage /></RouteGuard >} />
                            <Route path='/Login' element={<Login />} />
                            <Route path='/Logout' element={<Logout />} />
                            <Route path='/Troubleshooting' element={<Troubleshooting />} />
                            <Route path='/OsuLogin' element={<OsuLogin autoCompleteData={autoCompleteData} autoCompleteLoading={autoCompleteLoading} fetchAutoComplete={fetchAutoCompleteData} />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Layout>
                </MobileViewContext.Provider>
        </AppContext.Provider>
    );
}

export default App;
